import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// Transient notices, in the corner, out of the flow of the page.
//
// They used to be a line of text appended to whichever panel raised them,
// which meant the confirmation for something you did at the top of a long page
// appeared below the fold. A toast is in the same place whatever raised it.

const ToastContext = createContext(() => {});

// useToast returns notify(message, { tone }). Tone is 'note' or 'error'.
export const useToast = () => useContext(ToastContext);

// How long each tone stays. An error is both more important and more likely to
// be long, so it gets time to be read rather than the time a confirmation
// needs, which is only long enough to notice.
const DISMISS_MS = { note: 5000, error: 10000 };

// Enough to see a burst without burying the page under it. Older ones go.
const MAX_VISIBLE = 4;

// Matches the transition in admin.css; the node stays mounted this long after
// it is dismissed so it can fade rather than vanish.
const FADE_MS = 220;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const lastId = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((current) =>
      current.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast))
    );
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, FADE_MS);
  }, []);

  const notify = useCallback((message, { tone = 'note' } = {}) => {
    const text = String(message ?? '').trim();
    if (!text) return;

    lastId.current += 1;
    const toast = { id: lastId.current, message: text, tone };
    setToasts((current) => [...current, toast].slice(-MAX_VISIBLE));
  }, []);

  return (
    <ToastContext.Provider value={notify}>
      {children}
      <div className="toaster" role="region" aria-label="Notifications">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function Toast({ toast, onDismiss }) {
  // Hovering holds it open. Somebody with the pointer on a message is reading
  // it, and pulling it away mid-sentence is the one thing a toast must not do.
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (held || toast.leaving) return undefined;
    const timer = setTimeout(() => onDismiss(toast.id), DISMISS_MS[toast.tone] ?? DISMISS_MS.note);
    return () => clearTimeout(timer);
  }, [held, toast.id, toast.tone, toast.leaving, onDismiss]);

  return (
    <div
      className={`toast ${toast.tone}${toast.leaving ? ' leaving' : ''}`}
      // An error interrupts; a confirmation waits its turn.
      role={toast.tone === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <span className="toast-message">{toast.message}</span>
      <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => onDismiss(toast.id)}>
        ✕
      </button>
    </div>
  );
}
